export interface SubtitleCue {
  text: string;
  startMs: number;
  endMs: number;
}

const MAX_CUE_WORDS = 18;
const MIN_CLAUSE_WORDS = 4;

const SENTENCE_TERMINATOR = /[.!?\u2026][\u0022')\]]?$/;
const CLAUSE_TERMINATOR = /[,;:]$|[\u2014\u2013]/;
const CONJUNCTION = /^(ve|ile|and|but|so|or|yet|nor)$/i;

interface WordRef {
  start: number;
  end: number;
}

function tokenize(text: string): WordRef[] {
  const words: WordRef[] = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    words.push({ start: m.index, end: m.index + m[0].length });
  }
  return words;
}

function splitSentences(wordCount: number, isTerminator: (i: number) => boolean): number[][] {
  const sentences: number[][] = [];
  let current: number[] = [];
  for (let i = 0; i < wordCount; i++) {
    current.push(i);
    if (isTerminator(i)) {
      sentences.push(current);
      current = [];
    }
  }
  if (current.length > 0) sentences.push(current);
  return sentences;
}

function splitClauses(sentence: number[], isClauseBreak: (k: number) => boolean): number[][] {
  const units: number[][] = [];
  let current: number[] = [];
  for (let k = 0; k < sentence.length; k++) {
    current.push(sentence[k]);
    if (isClauseBreak(k) && current.length >= MIN_CLAUSE_WORDS) {
      units.push(current);
      current = [];
    }
  }
  if (current.length > 0) units.push(current);

  const final: number[][] = [];
  for (const unit of units) {
    if (unit.length <= MAX_CUE_WORDS) {
      final.push(unit);
    } else {
      for (let i = 0; i < unit.length; i += MAX_CUE_WORDS) {
        final.push(unit.slice(i, i + MAX_CUE_WORDS));
      }
    }
  }
  return final;
}

export function buildSubtitleCues(text: string, durationMs: number): SubtitleCue[] {
  const clean = (text || '').trim();
  if (!clean || durationMs <= 0) return [];

  const words = tokenize(clean);
  if (words.length === 0) return [];

  const sentences = splitSentences(words.length, (i) => {
    const token = clean.slice(words[i].start, words[i].end);
    return SENTENCE_TERMINATOR.test(token);
  });

  const units: number[][] = [];
  for (const sentence of sentences) {
    if (sentence.length <= MAX_CUE_WORDS) {
      units.push(sentence);
    } else {
      const clauses = splitClauses(sentence, (k) => {
        const token = clean.slice(words[sentence[k]].start, words[sentence[k]].end);
        return CLAUSE_TERMINATOR.test(token) || CONJUNCTION.test(token);
      });
      units.push(...clauses);
    }
  }

  const perWordMs = durationMs / words.length;
  const cues: SubtitleCue[] = [];
  let cursorMs = 0;
  units.forEach((unit, index) => {
    const startChar = words[unit[0]].start;
    const endChar = index < units.length - 1 ? words[units[index + 1][0]].start : clean.length;
    const unitMs = unit.length * perWordMs;
    cues.push({
      text: clean.slice(startChar, endChar),
      startMs: Math.round(cursorMs),
      endMs: Math.round(cursorMs + unitMs),
    });
    cursorMs += unitMs;
  });

  return cues;
}

export function buildSubtitleCuesFromTimepoints(text: string, timepoints: Array<{ markName: string; timeSeconds: number }>, durationMs: number): SubtitleCue[] {
  const clean = (text || '').trim();
  if (!clean || !timepoints || timepoints.length === 0) return buildSubtitleCues(text, durationMs);
  const words = tokenize(clean);
  if (words.length === 0) return [];
  // timepoints are w0..wN in order
  const wordStartMs: number[] = new Array(words.length).fill(0);
  timepoints.forEach((tp) => {
    const m = tp.markName?.match(/^w(\d+)$/);
    if (!m) return;
    const idx = parseInt(m[1], 10);
    if (idx >= 0 && idx < words.length) wordStartMs[idx] = Math.round(tp.timeSeconds * 1000);
  });
  const sentences = splitSentences(words.length, (i) => SENTENCE_TERMINATOR.test(clean.slice(words[i].start, words[i].end)));
  const units: number[][] = [];
  for (const sentence of sentences) {
    if (sentence.length <= MAX_CUE_WORDS) units.push(sentence);
    else {
      const clauses = splitClauses(sentence, (k) => {
        const token = clean.slice(words[sentence[k]].start, words[sentence[k]].end);
        return CLAUSE_TERMINATOR.test(token) || CONJUNCTION.test(token);
      });
      units.push(...clauses);
    }
  }
  const cues: SubtitleCue[] = [];
  units.forEach((unit, index) => {
    const startChar = words[unit[0]].start;
    const endChar = index < units.length - 1 ? words[units[index + 1][0]].start : clean.length;
    const startMs = wordStartMs[unit[0]] ?? 0;
    const nextUnitFirst = index + 1 < units.length ? units[index + 1][0] : words.length;
    const endMs = nextUnitFirst < words.length ? wordStartMs[nextUnitFirst] : durationMs;
    cues.push({ text: clean.slice(startChar, endChar).trim(), startMs, endMs });
  });
  return cues;
}